/**
 * XMO.09 D#2 — Analyst Review Client Service
 *
 * Provides client-side functions for the analyst review dashboard:
 * - Fetch pending/assigned reviews from Firestore
 * - Submit review resolution via callable Cloud Function
 *
 * Spec: HLX-XMO.09, Directive #2
 */

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  C_XMOD_ANALYST_REVIEWS,
  C_CAPTAIN_RESULTS,
  SC_CAPTAIN_RESULT_CORRECTIONS,
} from '../../constants/collections';

// ── Types (client-side mirrors of server types) ───────────────────

export type AnalystReviewState =
  | 'PENDING'
  | 'ASSIGNED'
  | 'IN_REVIEW'
  | 'RESOLVED'
  | 'REASSIGNED'
  | 'ESCALATED'
  | 'ARCHIVED';

export interface FlaggedItem {
  itemId: string;
  type: 'unrecognized_biomarker' | 'low_confidence_extraction' | 'unreadable_value' | 'retake_exceeded';
  triggerSource: 'XMO.03' | 'XMO.05' | 'CAPTAIN_LOW_CONFIDENCE';
  biomarkerName: string;
  extractedValue: string;
  confidenceScore: number;
  originalImageRef?: string;
  notes?: string;
  resolved: boolean;
  resolvedValue?: string;
}

export interface AnalystReviewDocument {
  reviewId: string;
  sessionId: string;
  patientId: string;
  status: AnalystReviewState;
  flaggedItems: FlaggedItem[];
  slaDeadline: { toDate(): Date } | null;
  createdAt: { toDate(): Date } | null;
  updatedAt: { toDate(): Date } | null;
  resolvedAt: { toDate(): Date } | null;
  escalationLevel: number;
  triggerSource: string;
  slaDurationHours?: number;
  resolution?: ReviewResolutionData;
}

export interface ResolvedItemInput {
  itemId: string;
  action: 'CORRECTED' | 'CONFIRMED' | 'REMOVED';
  originalValue: string;
  resolvedValue?: string;
  resolvedCanonicalName?: string;
  unit?: string;
  correctionReason?: string;
}

export interface NormalizerUpdateInput {
  rawName: string;
  normalizedName: string;
  action: 'ADD_ALIAS' | 'CONFIRM_EXISTING' | 'FLAG_FOR_REVIEW';
  category?: string;
  recommendedBy: string;
}

export interface ReviewResolutionData {
  resolvedItems: ResolvedItemInput[];
  normalizerUpdates: NormalizerUpdateInput[];
  notes: string;
}

export interface SubmitReviewPayload {
  reviewId: string;
  resolution: {
    resolvedItems: ResolvedItemInput[];
    normalizerUpdates: NormalizerUpdateInput[];
    notes: string;
    analystId: string;
    analystDisplayName: string;
  };
}

export interface SubmitReviewResult {
  success: boolean;
  correctionId: string | null;
  reviewId: string;
  resolvedItemCount: number;
}

// ── Fetch Reviews ─────────────────────────────────────────────────

/**
 * Fetches analyst reviews filtered by status.
 * For the analyst dashboard queue view.
 */
export async function fetchReviews(
  statuses: AnalystReviewState[],
): Promise<AnalystReviewDocument[]> {
  const reviewsRef = collection(db, C_XMOD_ANALYST_REVIEWS);
  const q = query(
    reviewsRef,
    where('status', 'in', statuses),
    orderBy('createdAt', 'asc'),
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({
    ...d.data(),
    reviewId: d.id,
  })) as AnalystReviewDocument[];
}

/**
 * Fetches a single review by ID.
 */
export async function fetchReview(reviewId: string): Promise<AnalystReviewDocument | null> {
  const docRef = doc(db, C_XMOD_ANALYST_REVIEWS, reviewId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return { ...snap.data(), reviewId: snap.id } as AnalystReviewDocument;
}

/**
 * Transitions a review from ASSIGNED to IN_REVIEW when analyst opens it.
 * Uses a transaction to prevent concurrent analysts claiming the same review.
 * If the review is not in ASSIGNED state, the transition is rejected.
 */
export async function markInReview(reviewId: string): Promise<void> {
  const docRef = doc(db, C_XMOD_ANALYST_REVIEWS, reviewId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(docRef);
    if (!snap.exists()) {
      throw new Error(`Review ${reviewId} not found`);
    }
    const current = snap.data();
    if (current.status !== 'ASSIGNED' && current.status !== 'REASSIGNED') {
      throw new Error(
        `Review ${reviewId} is in state ${current.status}, cannot transition to IN_REVIEW`,
      );
    }
    tx.update(docRef, {
      status: 'IN_REVIEW',
      updatedAt: serverTimestamp(),
    });
  });
}

// ── Submit Review ─────────────────────────────────────────────────

/**
 * Submits a review resolution via callable Cloud Function.
 * c3: submitReview(reviewId, resolution) → ReviewResolution
 * c4: corrections written to captain_results/{sessionId}/corrections/{correctionId}
 * c5: patient notification sent on completion
 */
export async function submitReview(
  reviewId: string,
  resolution: SubmitReviewPayload['resolution'],
): Promise<SubmitReviewResult> {
  const callable = httpsCallable<SubmitReviewPayload, SubmitReviewResult>(
    functions,
    'submitAnalystReview',
  );

  const result = await callable({ reviewId, resolution });
  return result.data;
}

// ── Fetch Corrections (verification) ──────────────────────────────

/**
 * Fetches corrections for a session from the corrections subcollection.
 * Used for verification in the dashboard that corrections were written.
 */
export async function fetchCorrections(sessionId: string) {
  const correctionsRef = collection(
    db,
    C_CAPTAIN_RESULTS,
    sessionId,
    SC_CAPTAIN_RESULT_CORRECTIONS,
  );
  const snapshot = await getDocs(correctionsRef);
  return snapshot.docs.map((d) => ({ ...d.data(), correctionId: d.id }));
}
